// Measured mode: every letter is an eighth note, and words are laid out so each
// one lands cleanly on a bar (or half a bar) of 4/4, padded with rests.
//
// The rules, in order:
//   • every word is followed by at least one eighth rest, so words never run
//     together. That means a word's slot is sized for its letters + 1: three
//     letters or fewer take a half bar, four letters take a whole bar;
//   • only a word of three letters or fewer may start halfway through a bar —
//     every longer word starts on a bar line and takes as many half bars as it
//     needs (8 letters = a bar and a half), so a short word after it can fill
//     the trailing half bar;
//   • anything left over inside a word's slot is a rest, and consecutive rests
//     are combined into the longest note values the bar allows (5 eighths of
//     silence = an eighth rest then a half rest).
export const EIGHTHS_PER_BAR = 8;
export const EIGHTHS_PER_HALF_BAR = 4;

// Rest values we engrave, in eighths: whole, half, quarter, eighth.
const REST_VALUES = [8, 4, 2, 1];

function slotFor(letterCount) {
  return (
    Math.ceil((letterCount + 1) / EIGHTHS_PER_HALF_BAR) * EIGHTHS_PER_HALF_BAR
  );
}

// Merge runs of eighth rests into real rest values. A value may only start on a
// position it divides evenly (so a half rest lands on beat 1 or 3) and may
// never cross a bar line.
function groupRests(steps) {
  let i = 0;
  while (i < steps.length) {
    if (!steps[i].rest) {
      i += 1;
      continue;
    }
    let end = i;
    while (end < steps.length && steps[end].rest) end += 1;
    let pos = i;
    while (pos < end) {
      const beat = pos % EIGHTHS_PER_BAR;
      const remaining = end - pos;
      const roomInBar = EIGHTHS_PER_BAR - beat;
      const value =
        REST_VALUES.find(
          (v) => v <= remaining && v <= roomInBar && beat % v === 0
        ) || 1;
      steps[pos].restStart = true;
      steps[pos].restEighths = value;
      for (let k = pos + 1; k < pos + value; k += 1) {
        steps[k].restStart = false;
        steps[k].restEighths = 0;
      }
      pos += value;
    }
    i = end;
  }
  return steps;
}

/**
 * Turns a play sequence into eighth-note slots with rests.
 * Returns { steps, bars } where each step is either
 *   { rest: false, ...entry, bar, beat } or
 *   { rest: true, bar, beat, restStart, restEighths }.
 * Playback walks every step (each one an eighth); notation draws a single rest
 * glyph per group, at the start of the group.
 */
export function measureSequence(sequence) {
  const byWord = [];
  sequence.forEach((entry) => {
    const last = byWord[byWord.length - 1];
    if (last && last.wordIdx === entry.wordIdx) last.entries.push(entry);
    else byWord.push({ wordIdx: entry.wordIdx, entries: [entry] });
  });

  const steps = [];
  let pos = 0; // in eighths from the start

  const pushRest = () => {
    steps.push({
      rest: true,
      bar: Math.floor(pos / EIGHTHS_PER_BAR),
      beat: pos % EIGHTHS_PER_BAR,
    });
    pos += 1;
  };

  byWord.forEach((word) => {
    const n = word.entries.length;
    const slot = slotFor(n);
    const roomInBar = EIGHTHS_PER_BAR - (pos % EIGHTHS_PER_BAR);

    if (n > 3 && roomInBar !== EIGHTHS_PER_BAR) {
      // Only a word of three letters or fewer may start on a half bar —
      // anything longer waits for the next bar line.
      for (let i = 0; i < roomInBar; i += 1) pushRest();
    }

    word.entries.forEach((entry) => {
      steps.push({
        ...entry,
        rest: false,
        bar: Math.floor(pos / EIGHTHS_PER_BAR),
        beat: pos % EIGHTHS_PER_BAR,
      });
      pos += 1;
    });
    for (let i = 0; i < slot - n; i += 1) pushRest();
  });

  // Round the last bar out so the export ends on a bar line.
  while (pos % EIGHTHS_PER_BAR !== 0) pushRest();

  return { steps: groupRests(steps), bars: pos / EIGHTHS_PER_BAR };
}
